import React, { useCallback } from "react";
import { notification } from "antd";
import { CheckCircle, X } from "@phosphor-icons/react";
import { useLocation, useNavigate } from "react-router-dom";

import { useNotificationSocket } from "hooks/useNotificationSocket";
import { notificationsApiService } from "services/notification.service";
import store from "stores";
import {
  getNotificationNavigationTarget,
  mapRealtimeNotificationToModel,
} from "utils/notification";
import { NotificationRealtimeEnvelope } from "services/notificationSocket.service";

const NotificationToastListener: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const handleNotificationToast = useCallback(
    (envelope: NotificationRealtimeEnvelope): void => {
      const item = envelope.notification;
      const mappedNotification = mapRealtimeNotificationToModel(item);
      const target = getNotificationNavigationTarget(mappedNotification);
      const key = `${item.notificationId}-${Date.now()}`;

      const handleOpenNow = async (): Promise<void> => {
        try {
          if (item.status === "unread") {
            await store
              .dispatch(
                notificationsApiService.endpoints.markNotificationAsRead.initiate(
                  {
                    id: item.notificationId,
                  },
                ),
              )
              .unwrap();
          }
        } catch (error) {
          console.error("Mark notification as read failed from toast:", error);
        } finally {
          api.destroy(key);
          navigate(target.pathname, { state: target.state });
        }
      };

      const isOnNotificationPage =
        location.pathname.startsWith("/notifications");
      const summaryText = item.body || item.title;
      const createdAtLabel = item.createdAt
        ? new Date(item.createdAt).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })
        : "";

      api.open({
        key,
        message: (
          <div className="text-[17px] font-semibold text-[#111827]">
            Thông báo
          </div>
        ),
        description: (
          <div className="mt-1">
            <div className="text-[15px] font-medium leading-6 text-[#374151]">
              {isOnNotificationPage
                ? "Bạn vừa nhận được thông báo mới"
                : summaryText}
            </div>
            {createdAtLabel ? (
              <div className="mt-2 text-[13px] text-[#9CA3AF]">
                {createdAtLabel}
              </div>
            ) : null}
          </div>
        ),
        icon: (
          <div className="mt-1 flex h-7 w-7 items-center justify-center rounded-full bg-[#E8F8ED]">
            <CheckCircle size={18} weight="fill" className="text-[#16A34A]" />
          </div>
        ),
        placement: "topRight",
        duration: 4.5,
        pauseOnHover: true,
        closeIcon: <X size={18} className="text-[#9CA3AF]" />,
        style: {
          width: 385,
          background: "#FFFFFF",
          borderRadius: 14,
          borderLeft: "4px solid #22C55E",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.16)",
          padding: "18px 20px 18px 18px",
        },
        onClick: () => {
          void handleOpenNow();
        },
      });
    },
    [api, location.pathname, navigate],
  );

  useNotificationSocket(handleNotificationToast);

  return <>{contextHolder}</>;
};

export default NotificationToastListener;
